import castArray from 'lodash/castArray';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import { addMiddleware } from 'redux-dynamic-middlewares';
import { persistStore } from 'redux-persist';

import { AppContext } from '../lib/Context';
import { composePromises } from '../lib/utils/promises';
import { registerHandlers, removeAndStopHandlers } from '../sagas/TaskManager';
import { getStore } from '../store/StoreAccessor';
import { createPersistConfig, injectReducer } from '../store/StoreAsync';
import Logger from '../tools/Logger';

import { addDomain, getDomain, getDomains } from './domainRegistry';
import { getInDomain } from './externalUtils';

const log = Logger.child({ component: 'domains/index', level: 'debug' }, true);

const GenericLoader = () => {
  return <span />;
};

/**
 * Adapter function which reconciles available module formats into a consistent value
 *
 * @param {object} obj - the incoming module object
 * @returns {object} a normalized module object
 */
function babelInterop(obj) {
  return obj && obj.__esModule ? obj.default : obj;
}

/**
 * Stops a domain with the provided name
 *
 * @param {string} name - of the domain to stop
 */
export function stopDomain(name) {
  const domain = getDomain(name);
  if (!domain) {
    log.warn(`startDomain: Domain does not exist: ${name}`);
  }

  if (domain.module) {
    domain.module.stop();
  } else {
    log.warn(`stopDomain: Domain not loaded: ${name}`);
  }
}

/**
 * Stop provided domains
 *
 * @param {Array} domains - the domains to stop
 */
export function stopDomains(domains) {
  domains.map(domain => stopDomain(domain.name));
}

/**
 * Starts a domain with the provided name
 *
 * @param {string} name of the domain to stop
 */
export function startDomain(name) {
  const domain = getDomain(name);
  if (!domain) {
    log.warn(`startDomain: Domain does not exist: ${name}`);
  }

  if (domain.module) {
    domain.module.start(getStore());
  } else {
    log.warn(`startDomain: Domain not loaded: ${name}`);
  }
}

/**
 * Start provided domains
 *
 * @param {Array} domains - the domains to start
 */
export function startDomains(domains) {
  domains.map(domain => startDomain(domain.name));
}

export function getLoadedDomains() {
  return getDomains(domain => {
    return domain.module && domain.module.loaded;
  }).map(domain => {
    return {
      name: domain.name,
      isStarted: domain.module.started,
    };
  });
}

/**
 * Traverse provided domains parents to determine whether or not each parent will be be loaded
 *
 * @note: We can't rely on current status as we may be in the middle of a recalculation
 * @param {object} domain - the domain to calculate from
 * @returns {boolean} - true if all parents should be conditionally loaded
 */
function willParentsBeLoadedAndStarted(domain) {
  return (function traverse(domainNode) {
    if (!domainNode) return true;

    if (!domainNode.cond(getStore().getState())) {
      return false;
    }

    return traverse(domainNode.parent);
  })(domain.parent);
}

export function startStopDomains() {
  getDomains(domain => domain.module).map(domain => {
    const isStarted = domain.module.started;
    const shouldBeStarted =
      domain.cond(getStore().getState(), isStarted) && willParentsBeLoadedAndStarted(domain);

    if (isStarted && !shouldBeStarted) {
      stopDomain(domain.name);
    } else if (!isStarted && shouldBeStarted) {
      startDomain(domain.name);
    }
  });
}

function loadUnloadedDomains() {
  const domainsToLoad = getDomains(domain => {
    return (
      !domain.module && domain.cond(getStore().getState()) && willParentsBeLoadedAndStarted(domain)
    );
  });

  return loadDomains(domainsToLoad);
}

export function recalculateDomains() {
  return composePromises(loadUnloadedDomains, startStopDomains)().catch(e => {
    throw new Error(e);
  });
}

/**
 * Domain Module blueprint used by Domain Module factory to produce
 * consistent enforceable interfaces to modules.
 */
const DomainModule = {
  name: null, // name of domain module (used to resolve path)
  actions: {},
  constants: {},
  selectors: {},
  components: {}, // hash of loaded components
  dependencies: [], // other domains to load first
  subdomains: [], // domains that this domain will load
  middleware: null, // middlewares that this domain will load
  reducer: null, // reducer(s) to add to rootReducer when loaded
  persisted: false, // whether or not this domain is persisted to localstorage
  persistTransforms: null, // in case the reducer would like to add transforms for its persisted data
  // this can be an array of filters/transforms or an object of names pointing to an
  // array of filters/transforms for specific reducers
  persistedReducers: null, // in case there are multiple reducers this should be an array of the names or if
  // the reducer's name does not reflect the domain name it must be set in an array
  started: false, // once a module is loaded it is started once it's handlers and reducer have been started
  loaded: false, // whether or not this domain is already loaded
  watchers: [], // watchers to register on start for this domain and then unregister on stop

  __registeredHandlers: [], // registered handlers (watchers) for this domain (added on start, removed on stop)

  runSaga(store, saga) {
    this.runtimeTasks = this.runtimeTasks || [];
    const task = store.runSaga(saga);
    this.runtimeTasks.push(task);
    return task;
  },

  /**
   * Sets a domain as loaded and executes the domains custom `onLoad` lifecycle event.
   *
   * @param {object} store - redux store
   * @returns {Promise} - returns `onLoad()` promise
   */
  load(store) {
    if (this.loaded) {
      return Promise.resolve();
    }

    log.trace(`[${this.name}]:DomainModule.load() - loading module`);
    this.loaded = true;

    return Promise.resolve(this.onLoad(store));
  },
  /**
   * Starts a domain and then executes the domains custom `onStart` lifecycle event.
   *
   * @param {object} store - the redux store
   */
  start(store) {
    if (this.started) return;

    log.trace(`[${this.name}]:DomainModule.start() - starting module`);
    this.started = true;

    if (this.watchers.length) {
      this.__registeredHandlers = registerHandlers(this.watchers);
    }

    log.trace(`[${this.name}]:DomainModule.start() - started`);
    this.onStart(store);
  },
  /**
   * Stops a domain and executes the domains custom `onStop` lifecycle event.
   *
   * @param {object} store - the redux store
   */
  stop() {
    if (!this.started) return;
    log.trace(`[${this.name}]:DomainModule.stop() - stopping module`);
    this.started = false;

    if (this.watchers.length) {
      removeAndStopHandlers(this.__registeredHandlers, this.name);
    }

    if (this.runtimeTasks) {
      this.runtimeTasks.forEach(task => task && task.cancel());
      this.runtimeTasks = [];
    }
    this.onStop();
  },
  /**
   * Custom lifecycle event for load.
   * Note: this event is only triggered once per domain
   *
   * @returns {Promise} - returns `onLoad` promise
   */
  onLoad() {
    return Promise.resolve();
  },
  onStart() {
    // post start lifecycle event for module implementors to register start events
  },
  onStop() {
    // post end lifecycle event for module implementors to de-register started events
  },
};

/**
 * Module factory used to create new modules
 *
 * @param {object} config - object used to overload/extend module blueprint
 * @returns {object} a newly created module
 */
export function createDomainModule(config) {
  if (!isString(config.name)) {
    throw new Error('createModule(): property name is a required');
  }
  return Object.assign(Object.create(DomainModule), config);
}

export function loadDomain(domainName, store = getStore(), domainLoader = defaultDomainLoader) {
  log.trace(`[${domainName}]:loadDomain()`);

  // short circuit recallers
  const domain = get(getDomain(domainName), ['module']);
  if (domain) {
    if (domain.promise) {
      return domain.promise;
    }

    return Promise.resolve(domain);
  }

  return domainLoader(domainName, store)();
}

export function loadDomains(domains, store = getStore(), loader = defaultDomainLoader) {
  const filteredDomains = domains.map(domain => {
    return domain.name || domain;
  });

  return Promise.all(
    filteredDomains.map(domain => {
      return loadDomain(domain, store, loader);
    }),
  );
}

export function defaultDomainLoader(name, store = getStore()) {
  return composePromises(
    importDomain(name),
    injectDomainReducers(store),
    injectDomainMiddleware(),
    handleDomainPersistence(store),
    loadDomainModule(store),
    makeStartDomainFunc(store),
  );
}

/**
 * Retrieves domain from Webpack owned code split. Uses Webpacks internal module cache to
 * return loaded modules immediately and avoids refetches
 *
 * @param {string} name - the string name of the domain to load
 * @param {Array} loaders - the loaders to use when importing
 * @returns {object} - the loaded domain module
 */
export function importDomain(name) {
  return () => {
    return new Promise((resolve, reject) => {
      const domainLoader = getDomain(name);

      if (!domainLoader) {
        return reject(`importDomain(): unable to find loader for ${name}`);
      }

      return resolve(
        domainLoader.loader().then(module => {
          const domainModule = babelInterop(module);
          addDomain(name, domainModule);
          return domainModule;
        }),
      );
    });
  };
}

export function makeStartDomainFunc(store) {
  return domainModule => {
    domainModule.start(store);
    return domainModule;
  };
}

/**
 * Injects the reducers of a domain module
 *
 * @param {object} store - the redux store
 * @param {Function} injector - the injector to use
 * @returns {Function} - composable function that injects a domainModules reducers
 */
export function injectDomainReducers(store, injector = injectReducer) {
  return domainModule => {
    log.trace(`[${domainModule.name}]:injectDomainReducers() - injecting reducers`);
    if (domainModule.reducer) {
      injector(store, domainModule.name, domainModule.reducer);
    }
    return domainModule;
  };
}

export function injectDomainMiddleware() {
  return domainModule => {
    if (domainModule.middleware) {
      addMiddleware(domainModule.middleware);
    }
    return domainModule;
  };
}

export function loadDomainModule(store) {
  return domainModule => {
    log.trace(`[${domainModule.name}]:loadDomainModule() - loading domainModule`);
    return domainModule.load(store).then(() => {
      return domainModule;
    });
  };
}

export function handleDomainPersistence(store) {
  return domainModule => {
    return new Promise((resolve /* , reject */) => {
      if (domainModule.persisted) {
        let reducersToPersist;
        if (
          Array.isArray(domainModule.persistedReducers) &&
          domainModule.persistedReducers.length
        ) {
          reducersToPersist = domainModule.persistedReducers;
        } else {
          reducersToPersist = [domainModule.name];
        }
        log.trace(
          `[${domainModule.name}]:handleDomainPersistence() - persistence started, ${
            reducersToPersist.length
          } reducer${reducersToPersist.length === 1 ? '' : 's'} will persist`,
        );
        const resolving = reducersToPersist.map(reducerName => {
          // configure persistence for domain
          const domainPersistConfig = {
            whitelist: [reducerName],
          };
          const persistConfig = createPersistConfig(domainPersistConfig);
          if (domainModule.persistTransforms) {
            // if this is an object of name maps to arrays for a named reducer we will use only
            // those even though it likely won't matter since the filter/transforms are themselves keyed
            // against reducers by name
            let xforms;
            if (Array.isArray(domainModule.persistTransforms)) {
              xforms = domainModule.persistTransforms;
            } else if (domainModule.persistTransforms[reducerName]) {
              const tmpXforms = domainModule.persistTransforms[reducerName];
              if (Array.isArray(tmpXforms)) xforms = tmpXforms;
              else xforms = [tmpXforms];
            }
            xforms.forEach(transform => {
              persistConfig.transforms.unshift(transform);
            });
          }

          // rehydrate domain from store
          return new Promise(persistSetupDone => {
            persistStore(store, persistConfig, (err /* , state */) => {
              log.trace(
                `[${domainModule.name}]:handleDomainPersistence(${reducerName}) - persistence finished`,
              );
              if (err) throw err;
              persistSetupDone();
            });
          });
        });
        Promise.all(resolving).then(() => {
          resolve(domainModule);
        });
      } else {
        log.trace(`[${domainModule.name}]:handleDomainPersistence() - not persisted`);
        resolve(domainModule);
      }
    });
  };
}

// eslint-disable-next-line valid-jsdoc
/**
 * HoC Component for lazy-loading other Components with optional loader
 *
 * @example
 * const importer = () => {
 *  return System.import(`${BASE_PATH}/src/containers/MyStations/MyStations`);
 * },
 * <AsyncComponent
 *   component='Button'     // relative path from components folder
 *   loader={GenericLoader} // component to use as loader (optional)
 *   importer={importer}    // a function that returns a system import that uses an absolute path
 * />
 */
export class AsyncComponent extends React.Component {
  static propTypes = {
    loader: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    importer: PropTypes.func.isRequired,
  };

  static defaultProps = {
    loader: <GenericLoader />,
  };

  constructor(props) {
    super(props);
    this.Lazy = React.lazy(this.props.importer);
  }

  render() {
    const loader = !React.isValidElement(this.props.loader) ? (
      <this.props.loader />
    ) : (
      this.props.loader
    );

    return (
      <Suspense fallback={loader}>
        <this.Lazy {...this.props} importer={null} />
      </Suspense>
    );
  }
}

// eslint-disable-next-line valid-jsdoc
/**
 * HoC Component for lazy-loading of Domain Modules. In addition,
 * the corresponding reducer and saga handlers will also be registered
 * and started.
 *
 * @example
 *
 * <AsyncDomain
 *    module='Search'         // relative path from modules folder
 *    component='SearchModal' // name of exported Component from Module
 *    loader={GenericLoader}  // component to use as loader (optional)
 * />
 */
export class AsyncDomain extends React.Component {
  static propTypes = {
    domain: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    subdomain: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    component: PropTypes.string,
    loader: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    onMount: PropTypes.func, // if this function returns true we won't render the component prop
    onUnmount: PropTypes.func,
    params: PropTypes.object, // optionally passed from a routing context
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  };

  static defaultProps = {
    onMount: () => false,
    onUnmount: () => false,
    params: {},
  };

  constructor(props, context) {
    super(props, context);
    this._isLoading = false;
    this._mounted = false;

    this.state = {
      Component: null,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.load();
  }

  componentWillUnmount() {
    this._mounted = false;
    this.stop();
    this.props.onUnmount(this.context.store);
  }

  static contextType = AppContext;

  load = () => {
    this._isLoading = true;

    const ensureDomain = () => {
      return isArray(this.props.domain)
        ? loadDomains(this.props.domain)
        : loadDomain(this.props.domain, this.context.store);
    };

    const ensureSubdomains = module => {
      return new Promise(resolve => {
        const domains = castArray(module).map(({ name }) => getDomain(name));
        const subdomains = compact(
          flatten(
            domains.map(domain => {
              return compact(castArray(this.props.subdomain)).filter(subdomain => {
                return getDomain(subdomain, domain);
              });
            }),
          ),
        );

        return subdomains.length
          ? loadDomains(subdomains).then(() => resolve(module))
          : resolve(module);
      });
    };

    if (isArray(this.props.domain)) {
      return composePromises(ensureDomain, ensureSubdomains)().then(() => {
        this._isLoading = false;
        this.setState({
          Component: null,
        });
      });
    }

    return composePromises(ensureDomain, ensureSubdomains)().then(module => {
      this._isLoading = false;
      this.module = module;
      const blockComponentLoad = this.props.onMount(
        this.context.store,
        this.context.router,
        this.props.params,
      );
      if (
        module.components &&
        (this.props.component || isFunction(this.props.children)) &&
        this._mounted &&
        !blockComponentLoad
      ) {
        this.setState({
          Component: getInDomain(`${this.props.domain}.components.${this.props.component}`),
        });
      }
    });
  };

  stop = () => {
    this._module && this._module.stop();
  };

  render() {
    const { Component } = this.state;
    const CustomLoader = this.props.loader;

    // component still loading
    if (this._isLoading) {
      if (this.props.loader) {
        return <CustomLoader />;
      }
      return <GenericLoader />;
    }

    // component requested
    if (Component) {
      return isFunction(this.props.children) ? (
        <Component {...this.props}>{this.props.children(this.module)}</Component>
      ) : (
        <Component {...this.props} />
      );
    } else if (isFunction(this.props.children)) {
      // custom child render method provided
      return this.props.children(this.module);
    } else if (this.props.children) {
      return this.props.children;
    }

    return null;
  }
}
